import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { FuseInput } from '/src/components';
import { useUserStore } from '/src/context';
import { FormErrors } from '/src/lib/types';

export const ResetPasswordPage: React.FC = observer(() => {
  /** Context **/
  const userStore = useUserStore();
  const navigate = useNavigate();
  const params = useParams();

  /** State **/
  const [newPassword, setNewPassword] = useState('');
  const [verifyPassword, setVerifyPassword] = useState('');
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  /** Computed **/
  const token = params.token;

  /** Methods **/

  /**
   * Validates the form data. If errors are detected, it saves them to `formErrors` and returns false.
   *
   * @returns Boolean indicating whether the data is valid
   */
  const validateForm = () => {
    const errors: FormErrors = {};

    if (!newPassword) errors.newPassword = 'New password is required';
    if (!verifyPassword) errors.verifyPassword = 'Verify password is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Resets the User's password.
   *
   * @remarks
   * Redirects to either the `success` or `invalid` password reset pages.
   */
  const resetUserPassword = async () => {
    if (!token) {
      navigate('/password/reset/invalid');
      return;
    }

    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    try {
      await userStore.resetUserPassword(token, newPassword, verifyPassword);

      navigate('/password/reset/success');
    } catch (err) {
      setError((err as Error).message);
      navigate('/password/reset/invalid');
    }
  };

  /** Render **/
  return (
    <Box className="row" data-ng-controller="PasswordController">
      <h3 className="col-md-12 text-center">Reset your password</h3>
      <Box component="div" className="col-xs-offset-2 col-xs-8 col-md-offset-5 col-md-2">
        <form className="signin form-horizontal" autoComplete="off">
          <fieldset>
            <FuseInput
              id="newPassword"
              label="New Password"
              type="password"
              value={newPassword}
              updateValue={(value) => setNewPassword(value)}
              required
              error={formErrors.newPassword}
            />

            <FuseInput
              id="verifyPassword"
              label="Verify Password"
              type="password"
              value={verifyPassword}
              updateValue={(value) => setVerifyPassword(value)}
              required
              error={formErrors.verifyPassword}
            />

            <Box component="div" className="text-center form-group">
              <button className="btn btn-large btn-primary" onClick={() => resetUserPassword()}>
                Update Password
              </button>
            </Box>

            {error && (
              <Box component="div" className="text-center text-danger">
                <strong>{error}</strong>
              </Box>
            )}
          </fieldset>
        </form>
      </Box>
    </Box>
  );
});
